<template>
  <div class="assembly-sheet__container landscape">

    <div 
      v-if="assemblySheet"
      class="assembly-sheet__info" 
      :class="{ 'container': !assemblySheet.attachment }">

      <div class="columns is-multiline">

        <div 
          class="column" 
          :class="assemblySheet.attachment ? 'is-half' : 'is-3'">
          <label class="label">Assembly Sheet No.</label>
          <div>{{ assemblySheet.assemblysheet_no }}</div>
        </div>

        <div 
          class="column" 
          :class="assemblySheet.attachment ? 'is-half' : 'is-3'">
          <label class="label">Project</label>
          <div>{{ assemblySheet.project.project_name }}</div>
        </div>

        <div 
          class="column" 
          :class="assemblySheet.attachment ? 'is-half' : 'is-3'">
          <label class="label">Assembly Sheet Condition</label>
          <v-text-field 
            :disabled="!editable"
            v-model="assemblySheet.condition"
            placeholder="Assembly sheet condition"
            class="noprint"
            @blur="updateAssemblySheet({
              assemblySheetId: assemblySheet.id,
              data: { condition: $event }
            })"/>
          <span class="printonly">
            {{ assemblySheet.condition }}
          </span>
        </div>

        <div 
          class="column" 
          :class="assemblySheet.attachment ? 'is-half' : 'is-3'">
          <label class="label">Description</label>
          <v-text-field 
            :disabled="!editable"
            v-model="assemblySheet.desc"
            placeholder="Assembly sheet description"
            class="noprint"
            @blur="updateAssemblySheet({
              assemblySheetId: assemblySheet.id,
              data: { desc: $event }
            })"/>
          <span class="printonly">
            {{ assemblySheet.desc }}
          </span>
        </div>

        <div 
          class="column" 
          :class="assemblySheet.attachment ? 'is-half' : 'is-3'">
          <label class="label">Total Output Parts</label>
          <v-text-field 
            required
            :disabled="!editable"
            v-model="assemblySheet.finished_qty"
            placeholder="Assembly sheet output parts"
            class="noprint"
            @blur="updateAssemblySheet({
              assemblySheetId: assemblySheet.id,
              data: { finished_qty: parseInt($event) || 0 }
            })"/>
          <span class="printonly">
            {{ assemblySheet.finished_qty }}
          </span>
        </div>

        <div
          class="column noprint" 
          :class="assemblySheet.attachment ? 'is-half' : 'is-3'">
          <label class="label">Assembly Sheet Upload</label>
          <v-file-upload
            placeholder="Change Upload File"
            file-type-validator=".*"
            @input="changeAttachment($event)">
          </v-file-upload>
        </div>

      </div>

      <hr>

      <div class="level">
        <div class="subtitle is-5 mb-0">Assembly Sheet Parts</div>
        <button 
          v-if="editable"
          @click="addingPart = true"
          class="button is-small is-primary noprint">
          Add a Part
        </button>
      </div>

      <article 
        v-if="assemblySheet.details.length == 0" 
        class="message is-info is-fullwidth noprint">
        <div class="message-body">
          There are no parts for this assembly sheet
        </div>
      </article>

      <table v-else class="table is-fullwidth has-small-font">

        <thead>
          <tr>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header.label }}
            </th>
            <th
              class="noprint" 
              v-if="editable">
            </th>
          </tr>
        </thead>

        <tbody v-if="assemblySheet">
          <tr    
            class="v-centered"           
            v-for="part in assemblySheet.details"
            :key="part.id">
            <td>
              <router-link 
                :to="{ 
                  name: 'CutSheetViewer', 
                  params: { cutSheetId: part.part.cutsheet.id }}">
                {{ part.part.cutsheet.cutsheet_no }}
              </router-link>
            </td>
            <td>
              {{ part.part.part_no }}
            </td>
            <td>
              {{ part.part.length_in_str }}
            </td>
            <td>
              {{ part.part.width_in_str }}
            </td>
            <td>
              {{ part.part.quantity | round(0) }}
            </td>
            <td>
              {{ part.part.total_sqft | round(2) }}
            </td>
            <td>
              <span v-if="part.id != editingDetailId">
                {{ part.section }}
              </span>
              <span v-else>
                <v-text-field 
                  v-model="part.section"
                  @blur="updatePart(part)"/>
              </span>
            </td>

            <td class="noprint" v-if="editable">
              <div class="buttons buttons-right">

                <button 
                  class="button is-small"
                  @click="toggleEdit(part.id)">
                  <span class="icon">
                    <i class="fas fa-edit has-text-grey"></i>
                  </span>
                </button>

                <button 
                  class="button is-small"
                  @click="deletePart(
                    { assemblySheetId: part.assemblysheet_id, detailId: part.id }
                  )">
                  <span class="icon">
                    <i class="fas fa-trash has-text-grey"></i>
                  </span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>

      </table>

    </div>

    <div 
      v-if="assemblySheet && assemblySheet.attachment" 
      class="assembly-sheet__preview">

      <img 
        v-if="attachmentType == 'img'"
        :src="assemblySheet.attachment"/>

      <iframe 
        v-if="attachmentType == 'pdf'"
        :src="assemblySheet.attachment"
        frameBorder="0">
      </iframe>

    </div>

    <add-assembly-detail-modal 
      v-if="assemblySheet"
      v-model="addingPart"
      :assembly-sheet-id="assemblySheet.id"
      :project-id="assemblySheet.project_id"/>

  </div>
</template>

<script>
import AddAssemblyDetailModal from '@/components/cutsheet/AddAssemblyDetailModal'
import VTextField from '@/components/base/VTextField'
import VFileUpload from '@/components/base/VFileUpload'
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { VTextField, AddAssemblyDetailModal, VFileUpload },
  data() {
    return {
      headers: [
        { label: 'Cutsheet #'},
        { label: 'Part #' },
        { label: 'L"' },
        { label: 'W"' },
        { label: 'Qty' },
        { label: 'Total Sqft' },
        { label: 'Section' }
      ],
      editingDetailId: null,
      addingPart: false
    }
  },
  computed: {
    ...mapState("assembly", ["assemblySheets"]),
    editable() {
      return true
    },
    assemblySheet() {
      return this.assemblySheets.filter(
        d => d.id == this.$route.params.assemblySheetId
      )[0];
    },
    attachmentType() {
      if (this.assemblySheet.attachment.includes(".pdf")) {
        return "pdf"
      } else {
        return "img"
      }
    }
  },
  methods: {
    ...mapActions("assembly", [
      "updateAssemblySheet", "deletePart", "updatePart", "addAttachment"
    ]),
    ...mapMutations("app", ["setNotification"]),
    toggleEdit(detailId) {
      if (this.editingDetailId == detailId) {
        this.editingDetailId = null
      } else {
        this.editingDetailId = detailId;
      }
    },
    changeAttachment(v) {
      let formData = new FormData();
      formData.append(
        'attachment', 
        v.file, 
        v.file.name
      );

      this.addAttachment({ 
        assemblySheetId: this.assemblySheet.id,  
        attachment: formData
      }).then(() => {
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Assembly sheet upload successfully changed'
        })
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/printable.scss';

.assembly-sheet {
  &__container {
    display: flex;
    flex-direction: row;
  }
  &__info {
    padding: 2em;
    flex-grow: 1;
    width: 45%;
  } 
  &__preview {
    width: 55%;
    height: calc(100vh - 52px);
    overflow: auto;
    background: whitesmoke;
    display: flex;
    flex-direction: row;
    align-items: center;
    img {
      width: 100%;
    }
    iframe {
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}

@media print {
  .assembly-sheet__info {
    padding: 0.5em !important;
    width: 50% !important;
  }
  .assembly-sheet__preview {
    width: 50% !important;
    height: 100vh !important;
    background: transparent !important;
  }
  .table {
    font-size: 0.8em !important;
  }
  .columns {
    margin-top: 0.5em !important;
  }
  .column {
    font-size: 0.8em !important;
    padding: 0.5em 0.5em;
    .label {
      font-size: 0.85em !important;
    }
  }
  .level {
    margin-bottom: 0.5em !important;
  }
  .subtitle {
    font-size: 0.9em !important;
  }
  hr {
    margin: 0.75em 0em !important;
  }
}
</style>